import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils/cn";

export interface PopoverProps {
  /** Element the popover is positioned under. Mirrors MUI's anchorEl API. */
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Horizontal alignment relative to the anchor (logical). */
  align?: "start" | "end" | "center";
  /** Fixed width in px; defaults to auto (content-sized). */
  width?: number;
}

/**
 * Floating panel anchored to a trigger — design-system replacement for MUI
 * `<Popover anchorEl … >`. Portal, RTL-aware, clamps fully into the viewport
 * (never cut off), closes on outside-click / Escape. For action lists use ui/Menu.
 */
const Popover = ({
  anchorEl,
  open,
  onClose,
  children,
  className,
  align = "start",
  width,
}: PopoverProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setPos(null);
      return;
    }
    const r = anchorEl.getBoundingClientRect();
    const rtl = document.documentElement.getAttribute("dir") !== "ltr";
    const M = 8;
    const el = ref.current;
    const w = width ?? el?.offsetWidth ?? r.width;
    const h = el?.offsetHeight ?? 0;

    // preferred left from logical alignment
    let left: number;
    if (align === "center") left = r.left + r.width / 2 - w / 2;
    else if ((align === "start") === rtl)
      left = r.right - w; // start in RTL / end in LTR = right edge
    else left = r.left;
    left = Math.min(Math.max(left, M), window.innerWidth - w - M);

    let top = r.bottom + 6;
    if (h && top + h > window.innerHeight - M) {
      const above = r.top - 6 - h;
      top = above >= M ? above : Math.max(M, window.innerHeight - h - M);
    }
    setPos({ top, left });
  }, [open, anchorEl, align, width, children]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-popover" onClick={onClose}>
      <div
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={
          pos
            ? { position: "fixed", top: pos.top, left: pos.left, width }
            : {
                position: "fixed",
                top: 0,
                left: 0,
                visibility: "hidden",
                width,
              }
        }
        className={cn(
          "max-h-[80vh] animate-scale-in overflow-y-auto rounded-2xl border border-border-subtle bg-surface p-4 text-menu shadow-e3",
          className
        )}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Popover;
